import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";

import { CollapsibleSection } from "@/components/layout/collapsible-section";
import { Skeleton } from "@/components/ui/skeleton";

import { README_FILE, useProcessModelFile } from "./use-process-model";

/** 設計画面と同じファイルを読む（書く場所が 1 つなら食い違わない）。 */

const MarkdownView = lazy(() =>
  import("@/components/markdown/markdown-view").then((m) => ({
    default: m.MarkdownView,
  })),
);

export type ProcessDescriptionProps = {
  readonly processModelId: string;
  /** 既定で開くか。判断材料として読ませたい画面では true にする。 */
  readonly defaultOpen?: boolean;
};

/**
 * プロセス（案件種別）の説明。モデルの README.md を読み取り専用で出す。
 *
 * 編集版（`model-readme.tsx`）の読み取り専用版。同じファイルを見るので隣に置く。
 * ここで編集させないのは、1 件の案件を見ている人が種別全体の説明を書き換えて
 * しまわないため（編集は設計画面の役割）。
 *
 * 使う場面で既定の開閉が違う。
 * - 案件詳細 … 畳む。案件ごとに変わらない情報なので毎回開く必要がない
 * - 起票（案件種別の詳細）… 開く。「何を始めるのか」を決める場面なので
 *
 * 開閉は components/layout/collapsible-section.tsx に任せる。
 */
export function ProcessDescription({
  processModelId,
  defaultOpen = false,
}: ProcessDescriptionProps) {
  const { t } = useTranslation("common");
  const file = useProcessModelFile(processModelId, README_FILE);

  const contents = file.data?.contents ?? "";

  return (
    <CollapsibleSection
      id="process-description"
      title={t("caseDetail.sections.process")}
      defaultOpen={defaultOpen}
    >
      <p className="text-muted-foreground text-xs">
        {t("caseDetail.processDescriptionNote")}
      </p>

      {file.isPending ? <Skeleton className="h-40 w-full" /> : null}

      {file.isError || (!file.isPending && contents === "") ? (
        <p className="text-muted-foreground text-sm">
          {t("processDesign.readme.empty")}
        </p>
      ) : null}

      {contents === "" ? null : (
        <Suspense fallback={<Skeleton className="h-40 w-full" />}>
          <MarkdownView source={contents} />
        </Suspense>
      )}
    </CollapsibleSection>
  );
}
